import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import { QueryVal } from 'Helpers/Query';

const IdentityVerificationReviewsContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const identityVerificationReviewsFilterLink = 'identityVerificationReviewsFilterLink';
const identityVerificationReviewsSelfLink = 'identityVerificationReviewSelfLink';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    identityVerificationReviewsCollection: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        identityVerificationReviewsCollection: action.identityVerificationReviewsCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    default: {
      throw new Error(`Unhandled IdentityVerificationReviewsContext action type: ${action.type}`);
    }
  }
};

const isReviewSet = () => {
  const url = new URL(window.location.href);
  return url.searchParams.has(identityVerificationReviewsSelfLink);
};

const IdentityVerificationReviewsProvider = ({
  agentResource,
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const loadIdentityVerificationReviews = useCallback(async () => {
    try {
      dispatch({
        type: INITIALIZE_STARTED,
      });

      let link = agentResource?.relationship(fr.relationships.identityVerificationReviews.name).link('related').url();

      if (isReviewSet()) {
        link = QueryVal(identityVerificationReviewsSelfLink);
      }

      const collection = await fr.http.get(link);

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        identityVerificationReviewsCollection: collection,
      });
    } catch (err) {
      dispatch({
        type: INITIALIZE_FAILED,
        error: err,
      });
    }
  }, [agentResource]);

  const refreshIdentityVerificationReviewsCollection = useCallback(async () => {
    await loadIdentityVerificationReviews();
  }, [agentResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      identityVerificationReviewsCollection: state.identityVerificationReviewsCollection,
      refreshIdentityVerificationReviewsCollection,
      identityVerificationReviewsFilterLink,
      identityVerificationReviewsSelfLink,
    };
  }, [
    state,
  ]);

  useComponentDidMount(() => {
    if (!agentResource.relationship(fr.relationships.identityVerificationReviews.name)) {
      return;
    }

    loadIdentityVerificationReviews();
  });

  return (
    <IdentityVerificationReviewsContext.Provider value={memoizedValue}>
      {children}
    </IdentityVerificationReviewsContext.Provider>
  );
};

// Use IdentityVerificationReviews State Context
const useIdentityVerificationReviewsContext = () => {
  const context = React.useContext(IdentityVerificationReviewsContext);
  if (context === undefined) {
    throw new Error('useIdentityVerificationReviewsContext must be used within a IdentityVerificationReviewsProvider');
  }
  return context;
};

IdentityVerificationReviewsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  agentResource: PropTypes.object.isRequired,
};

export {
  IdentityVerificationReviewsProvider,
  useIdentityVerificationReviewsContext,
};
